import { useSearchParams } from "react-router-dom";
import { useContent } from "@/hooks/useContent";
import { useProfile } from "@/hooks/useUserData";
import { buildWhatsAppMessage } from "@/components/WhatsAppBox";
import { toast } from "sonner";
import { MessageCircle, Users, Send, Video, MapPin, Crown, Quote, PartyPopper } from "lucide-react";

const AREA_IMAGES = {
  mindset: "/academy-mindset.jpeg",
  wellness: "/academy-fitness.jpeg",
  beauty: "/academy-beauty.jpeg",
  relazioni: "/academy-amore.jpeg",
};

const BENEFIT_ICONS = [Users, Send, Video, MapPin];

export function Academy() {
  const { data: content } = useContent();
  const { data: profile } = useProfile();
  const [searchParams] = useSearchParams();
  const paymentSuccess = searchParams.get("payment") === "success";

  if (!content) return <div className="flex justify-center pt-20"><div className="w-9 h-9 rounded-full border-4 border-[#6B2247] border-t-[#E32C8C] animate-spin" /></div>;

  const academy = content.academy;
  const waMessage = profile?.has_test
    ? buildWhatsAppMessage(profile, content.pillars)
    : "Ciao Elena! Vorrei più informazioni sulla Academy Be Woman. 💗";
  const waLink = `https://wa.me/${content.whatsapp_number}?text=${encodeURIComponent(waMessage)}`;

  const choosePlan = (plan) => {
    if (plan.stripe_url) {
      window.location.href = plan.stripe_url;
    } else {
      toast.info(`${plan.name} · ${plan.price} — link di pagamento in arrivo a breve`);
    }
  };

  return (
    <div className="bw-fade">
      {/* Payment success welcome */}
      {paymentSuccess && (
        <div
          data-testid="academy-payment-success"
          className="rounded-2xl p-5 mb-5 border border-[#E32C8C]/50 bw-card"
          style={{ background: "linear-gradient(135deg,rgba(227,44,140,0.28),rgba(142,26,82,0.20))" }}
        >
          <div className="flex items-center gap-2 mb-2 text-[#FF8FC7]">
            <PartyPopper className="w-5 h-5" />
            <span className="text-xs uppercase tracking-widest font-semibold">Benvenuta nella Academy</span>
          </div>
          <h2 className="font-serif text-3xl font-bold text-[#F6DCEA] mb-2">Grazie, il tuo posto è confermato!</h2>
          <p className="text-[15px] text-[#EAC6D9] leading-relaxed">
            Verrai contattata a breve dal nostro staff che ti invierà i link di accesso ai nostri canali.
          </p>
        </div>
      )}

      {/* Hero */}
      <div className="rounded-2xl p-6 mb-5 text-white shadow-lg" style={{ background: "linear-gradient(135deg,#E32C8C 0%,#8E1A52 55%,#3A0C21 100%)" }}>
        <div className="flex items-center gap-2 mb-3 opacity-90">
          <Crown className="w-4 h-4" />
          <span className="text-xs uppercase tracking-widest font-semibold">Academy</span>
        </div>
        <h1 className="font-serif text-4xl font-bold mb-2 leading-tight">{academy.headline}</h1>
        <p className="font-serif text-xl text-white/95 italic mb-3">{academy.subheadline}</p>
        <p className="text-[15px] text-white/90 leading-relaxed">{academy.intro}</p>
      </div>

      {/* Areas */}
      <h2 className="font-serif text-3xl font-semibold text-[#F6DCEA] mb-2 px-1">Le quattro aree</h2>
      <p className="text-sm text-[#EAC6D9] mb-4 px-1 leading-relaxed">{academy.areas_intro}</p>
      <div className="space-y-4 mb-7">
        {academy.areas.map((a) => (
          <div key={a.id} data-testid={`academy-area-${a.id}`} className="rounded-2xl bg-[#2A0A1B] border border-[#E32C8C]/35 bw-card overflow-hidden">
            <img src={AREA_IMAGES[a.id]} alt={a.name} className="w-full aspect-square object-cover" />
            <div className="flex flex-wrap gap-2 p-4">
              {a.pros.map((pro, i) => (
                <span key={i} className="text-xs font-medium text-[#F7A8CE] bg-[#E32C8C]/15 border border-[#E32C8C]/30 rounded-full px-3 py-1">
                  {pro}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Benefits */}
      <h2 className="font-serif text-3xl font-semibold text-[#F6DCEA] mb-2 px-1">Cosa trovi dentro</h2>
      <p className="text-sm text-[#EAC6D9] mb-4 px-1 leading-relaxed">{academy.benefits_intro}</p>
      <div className="space-y-3 mb-7">
        {academy.benefits.map((b, i) => {
          const Icon = BENEFIT_ICONS[i] || Users;
          return (
            <div key={i} data-testid={`academy-benefit-${i}`} className="rounded-2xl bg-[#2A0A1B] border border-[#E32C8C]/35 bw-card p-4 flex gap-3">
              <span className="w-11 h-11 rounded-full bg-[#E32C8C]/10 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-[#E32C8C]" />
              </span>
              <div>
                <p className="font-semibold text-[#F6DCEA] leading-snug">
                  <span className="text-[#E32C8C] font-bold mr-1.5">{b.num}</span>{b.title}
                </p>
                <p className="text-sm text-[#EAC6D9] leading-relaxed mt-1">{b.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Plans */}
      <h2 className="font-serif text-3xl font-semibold text-[#F6DCEA] mb-2 px-1">Scegli quanto tempo dare a te stessa</h2>
      <p className="text-sm text-[#EAC6D9] mb-4 px-1 leading-relaxed">{academy.plans_intro}</p>
      <div className="space-y-3 mb-3">
        {academy.plans.map((plan) => (
          <div
            key={plan.id}
            data-testid={`academy-plan-${plan.id}`}
            className="rounded-2xl p-5 border-2 relative bg-[#2A0A1B]"
            style={{ borderColor: plan.highlight ? "#E32C8C" : "#6B2247" }}
          >
            {plan.note && (
              <span
                className="absolute -top-3 left-5 text-[11px] font-bold uppercase tracking-wide px-3 py-1 rounded-full text-white"
                style={{ background: plan.highlight ? "linear-gradient(135deg,#E32C8C,#8E1A52)" : "#EAC6D9" }}
              >
                {plan.note}
              </span>
            )}
            <div className="flex items-end justify-between mb-1 mt-1">
              <h3 className="font-serif text-2xl font-semibold text-[#F6DCEA]">{plan.name}</h3>
              <div className="text-right">
                <p className="font-serif text-3xl font-bold text-[#E32C8C]">{plan.price}</p>
                {plan.per_month && <p className="text-xs text-[#EAC6D9]">{plan.per_month}</p>}
              </div>
            </div>
            {plan.save && <p className="text-sm text-[#FF7AB8] font-semibold mb-3">{plan.save}</p>}
            <button
              onClick={() => choosePlan(plan)}
              data-testid={`academy-plan-cta-${plan.id}`}
              className="w-full mt-2 font-semibold py-3 px-6 rounded-full transition-all active:scale-[0.98]"
              style={
                plan.highlight
                  ? { background: "linear-gradient(135deg,#E32C8C,#8E1A52)", color: "#fff", boxShadow: "0 8px 20px rgba(214,36,122,0.25)" }
                  : { backgroundColor: "#00000000", color: "#F6DCEA", border: "1px solid rgba(214,36,122,0.3)" }
              }
            >
              {plan.cta}
            </button>
          </div>
        ))}
      </div>
      <p className="text-sm text-[#EAC6D9] italic text-center leading-relaxed mb-7 px-2">{academy.plans_footer}</p>

      {/* Closing quote */}
      <div className="rounded-2xl bg-[#2A0A1B] border border-[#E32C8C]/35 bw-card p-5 mb-5 relative">
        <Quote className="w-6 h-6 text-[#E32C8C]/40 mb-2" />
        <p className="font-serif text-xl text-[#F6DCEA] italic leading-relaxed">{academy.closing_quote}</p>
      </div>

      {/* Elena intro */}
      <div className="rounded-2xl bg-[#2A0A1B] border border-[#E32C8C]/35 bw-card p-5 mb-4 flex items-center gap-4" data-testid="academy-elena-card">
        <span className="w-16 h-16 rounded-full overflow-hidden shrink-0 border-2 border-[#6B2247]">
          <img src="/elena.jpg?v=2" alt="Elena" className="w-full h-full object-cover" style={{ objectPosition: "center top" }} />
        </span>
        <div>
          <p className="font-serif text-2xl font-semibold text-[#F6DCEA] leading-none">Elena</p>
          <p className="text-sm text-[#EAC6D9] mt-1 leading-snug">La tua coach nella Academy. Scrivile per capire da dove iniziare.</p>
        </div>
      </div>

      {/* Final CTA */}
      <a
        href={waLink}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="academy-whatsapp-cta"
        className="w-full bg-gradient-to-r from-[#E32C8C] to-[#8E1A52] text-white font-semibold py-3.5 px-6 rounded-full shadow-lg shadow-[#E32C8C]/25 bw-glow hover:opacity-95 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
      >
        <MessageCircle className="w-5 h-5" /> Scrivi a Elena su WhatsApp
      </a>
    </div>
  );
}
